import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { isEmptyNode } from '@app/lib/is-empty-node/isEmptyNode';
import { useTranslations } from 'next-intl';
import { ChangeEvent, ReactNode, useCallback } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

export interface ChoiceButtonOption {
    label: ReactNode;
    value: string;
    details?: ReactNode;
    hideDetails?: boolean;
    hiddenInputs?: ReactNode;
    disabled?: boolean;
}

interface Props {
    name: string;
    options: ChoiceButtonOption[];
    variant?: 'text' | 'default';
    type?: 'radio' | 'checkbox';
    label?: ReactNode;
    error?: ViolationError;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    value?: string | string[];
    defaultChecked?: string | string[];
    inputContainerClassName?: string;
}

export function ChoiceButtonInput({
    name,
    options,
    type = 'radio',
    variant = 'default',
    label,
    error,
    onChange,
    value,
    defaultChecked,
    inputContainerClassName,
}: Props) {
    const t = useTranslations();

    const [showError, handleChange] = useShowErrorState(error, onChange);

    const isControlled = value !== undefined;

    const isChecked = useCallback(
        (optionValue: string): boolean => {
            if (isControlled) {
                if (type === 'radio') {
                    return value === optionValue;
                } else {
                    return Array.isArray(value) && value.includes(optionValue);
                }
            } else {
                if (type === 'radio') {
                    return defaultChecked === optionValue;
                } else {
                    return Array.isArray(defaultChecked) && defaultChecked.includes(optionValue);
                }
            }
        },
        [isControlled, type, value, defaultChecked],
    );

    const groupLabelId = `group-label-${name}`;

    return (
        <div className="flex flex-col gap-2">
            {label != null && (
                <span id={groupLabelId} className="text-sm">
                    {label}
                </span>
            )}
            <div
                role="group"
                aria-labelledby={label != null ? groupLabelId : undefined}
                className={twMerge('flex flex-col gap-4', variant === 'text' && 'gap-1')}>
                {options.map(option => (
                    <div
                        key={option.value}
                        className={twMerge(
                            'group flex flex-grow flex-col',
                            variant === 'default' &&
                                'min-h-[4.375rem] rounded-lg border border-neutral-150 bg-white p-6 shadow',
                            variant === 'default' &&
                                'has-[.choice-input:checked]:border-primary-500 has-[.choice-input:checked]:shadow-lg',
                            variant === 'text' && 'items-start',
                            option.disabled && 'opacity-70',
                        )}>
                        <label
                            htmlFor={`option-${name}-${option.value}`}
                            className={twMerge(
                                'peer flex items-center justify-between gap-4',
                                variant === 'text' && 'flex-row-reverse justify-start',
                                option.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                            )}>
                            <span
                                className={twMerge('w-11/12 font-medium', variant === 'text' && 'w-auto font-normal')}>
                                {option.label}
                            </span>
                            <div
                                className={twJoin(
                                    'relative flex size-5 shrink-0 items-center justify-center border border-neutral-400',
                                    type === 'radio' && 'rounded-full has-[:checked]:border-primary-500',
                                    type === 'checkbox' &&
                                        'rounded has-[:checked]:border-primary-500 has-[:checked]:bg-primary-500',
                                    inputContainerClassName,
                                )}>
                                <input
                                    id={`option-${name}-${option.value}`}
                                    name={name}
                                    type={type}
                                    className={twJoin(
                                        'peer absolute inset-0 cursor-pointer appearance-none',
                                        'choice-input',
                                        option.disabled && 'cursor-not-allowed',
                                    )}
                                    value={option.value}
                                    onChange={handleChange}
                                    disabled={option.disabled}
                                    {...(isControlled
                                        ? { checked: isChecked(option.value) }
                                        : { defaultChecked: isChecked(option.value) })}
                                />
                                {type === 'radio' && (
                                    <div
                                        aria-hidden="true"
                                        className="size-2 rounded-full peer-checked:bg-primary-500"></div>
                                )}
                                {type === 'checkbox' && (
                                    <svg
                                        aria-hidden="true"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M5.91308 9.03516H5.91326C5.99256 9.03525 6.07081 9.05331 6.14213 9.08798C6.21341 9.12262 6.27591 9.17295 6.32497 9.2352L5.91308 9.03516ZM5.91308 9.03516H5.09394C4.89031 9.03516 4.7766 9.26975 4.90232 9.4292L4.90237 9.42927L8.11214 13.4957L8.11218 13.4957C8.32247 13.7619 8.72568 13.7611 8.93667 13.4962L8.93708 13.4957L14.6597 6.24398C14.6598 6.24378 14.66 6.24358 14.6602 6.24338C14.7884 6.08295 14.6699 5.85 14.4689 5.85H13.6498C13.4894 5.85 13.3367 5.9235 13.2376 6.05044C13.2375 6.05055 13.2374 6.05066 13.2373 6.05077L8.52403 12.0216M5.91308 9.03516L8.52403 12.0216M8.52403 12.0216L6.32506 9.23531L8.52403 12.0216Z"
                                            fill="white"
                                            stroke="white"
                                            strokeWidth="0.3"
                                        />
                                    </svg>
                                )}
                            </div>
                        </label>
                        {!isEmptyNode(option.details) && (
                            <div
                                className={twJoin(
                                    'mt-4 border-t border-t-neutral-300 pt-4',
                                    'hidden group-has-[.choice-input:checked]:block',
                                    'break-words',
                                )}>
                                {option.details}
                            </div>
                        )}

                        {option.hiddenInputs != null && option.hiddenInputs}
                    </div>
                ))}
            </div>
            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
        </div>
    );
}
